import { useState, useEffect } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Instagram, 
  X, 
  Shield, 
  TrendingUp, 
  Users, 
  Mail, 
  User, 
  Phone, 
  MapPin, 
  Lock,
  CreditCard,
  PiggyBank,
  BarChart3,
  Wallet,
  Globe,
  ChevronRight,
  Play
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

// Background images for slider
const BACKGROUND_IMAGES = [
  "https://customer-assets.emergentagent.com/job_65470254-3ca3-4252-a1bd-53df00d02f41/artifacts/3rbal74v_phi4._white_--ar_32_--raw_--stylize_50_--v_7_ad08fdcf-e462-463f-a94f-78bd1ebaaeeb.png",
  "https://customer-assets.emergentagent.com/job_65470254-3ca3-4252-a1bd-53df00d02f41/artifacts/smv3xcyn_phi4._continuar_con_el_fondo_--ar_4129_--raw_--stylize_50_--v_7_f98b4390-e51f-4494-8806-8726af931c1e.png"
];

// Logos
const LOGO_MAIN = "https://customer-assets.emergentagent.com/job_simple-portfolio-hub/artifacts/dimu6asb_logo.svg";
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_simple-portfolio-hub/artifacts/ctb6t1va_logo.svg";

// Menu sections
const menuSections = [
  { id: "quienes-somos", name: "Quienes Somos" },
  { id: "portfolio", name: "Soluciones Financieras" },
  { id: "seguridad", name: "Regulación" },
  { id: "custodios", name: "Custodios" },
  { id: "faq", name: "Preguntas Frecuentes" },
  { id: "contacto", name: "Contacto" }
];

// Portfolio items
const portfolioItems = [
  { icon: TrendingUp, title: "Inversiones", description: "Gestiona tus inversiones de forma inteligente", growth: "+24%", color: "bg-emerald-50", iconColor: "text-emerald-600" },
  { icon: CreditCard, title: "Pagos Digitales", description: "Transferencias rápidas y seguras", growth: "+45%", color: "bg-sky-50", iconColor: "text-sky-600" },
  { icon: PiggyBank, title: "Ahorro", description: "Alcanza tus metas financieras", growth: "+32%", color: "bg-amber-50", iconColor: "text-amber-600" },
  { icon: BarChart3, title: "Analytics", description: "Visualiza tu progreso financiero", growth: "+18%", color: "bg-violet-50", iconColor: "text-violet-600" },
  { icon: Wallet, title: "Wallet Digital", description: "Tu dinero siempre disponible", growth: "+56%", color: "bg-rose-50", iconColor: "text-rose-600" },
  { icon: Globe, title: "Internacional", description: "Opera sin fronteras", growth: "+38%", color: "bg-teal-50", iconColor: "text-teal-600" }
];

// Security features
const securityFeatures = [
  { icon: Shield, title: "Encriptación de Datos", description: "Tus datos protegidos con encriptación de grado militar AES-256" },
  { icon: Lock, title: "Autenticación Biométrica", description: "Acceso seguro mediante huella digital y reconocimiento facial" },
  { icon: Users, title: "Verificación 2FA", description: "Doble factor de autenticación para máxima seguridad" }
];

// FAQ items
const faqItems = [
  { question: "¿Qué es Abacco Financial?", answer: "Es una compañía fintech enfocada en administración de finanzas personales y asesoría financiera. Nuestro objetivo es acompañar a los clientes en la toma de decisiones estratégicas para el crecimiento y protección de su patrimonio." },
  { question: "¿En qué mercados se puede invertir a través de Abacco Financial?", answer: "A través de Abacco Financial, los clientes pueden acceder principalmente al mercado financiero de Estados Unidos, uno de los sistemas bursátiles más sólidos y regulados del mundo, con oportunidades de inversión en empresas globales y diversos instrumentos financieros." },
  { question: "¿Abacco Financial opera de forma legal en Ecuador?", answer: "Sí. Abacco Financial opera legalmente en la República del Ecuador, bajo la supervisión de la Superintendencia de Bancos del Ecuador, cumpliendo con el marco regulatorio vigente, las normas de prevención de lavado de activos y las obligaciones tributarias correspondientes." },
  { question: "¿Las inversiones cuentan con respaldo o supervisión internacional?", answer: "Sí. Las operaciones se desarrollan dentro de un entorno regulado por entidades del sistema financiero estadounidense, lo que aporta transparencia, seguridad y estándares de supervisión reconocidos internacionalmente." },
  { question: "¿Quién puede invertir con Abacco Financial?", answer: "Los servicios de Abacco Financial están dirigidos a personas naturales, profesionales, empresarios y empresas que buscan estructurar, diversificar y fortalecer su patrimonio mediante estrategias de inversión." },
  { question: "¿Qué tipo de asesoría ofrece Abacco Financial?", answer: "Abacco Financial ofrece a través de tecnologías digitales y plataformas tecnológicas servicios de asesoría financiera al cliente, en función de sus necesidades y objetivos financieros." },
  { question: "¿Abacco Financial ofrece educación financiera?", answer: "Sí, La educación financiera es uno de los pilares de Abacco Financial. Nuestros asesores y la innovación tecnológica desarrollan charlas, programas financieros formativos y contenidos educativos para empresas, instituciones y personas interesadas en adquirir y mejorar su cultura financiera." },
  { question: "¿Dónde se encuentran las oficinas de Abacco Financial?", answer: "Abacco Financial cuenta con presencia en varias ciudades del país, incluyendo Quito (Casa Matriz), Guayaquil, Loja e Ibarra, ofreciendo atención directa y acompañamiento profesional." },
  { question: "¿Cómo puedo contactarme con Abacco Financial?", answer: "Puedes comunicarte con nosotros a través de nuestras oficinas, canales digitales o solicitando una reunión con uno de nuestros asesores financieros para recibir orientación personalizada." }
];

// Menu Fullscreen Component
const MenuFullscreen = ({ isOpen, onClose, onSelectSection, activeSection, onBack }) => {
  
  // Portfolio Content
  const PortfolioContent = () => (
    <div className="flex flex-col h-full w-full relative">
      <div 
        className="space-y-3 w-full overflow-y-auto flex-1 -mr-4 pr-4"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(0,0,0,0.2) transparent'
        }}
      >
        <h2 className="font-['Outfit'] text-xl sm:text-2xl font-semibold text-slate-900 mb-2">Abacco Financial</h2>
        
        <p className="text-slate-700 text-xs sm:text-sm leading-relaxed mb-2">
          Ofrecemos un conjunto de servicios orientados a estructurar, organizar y fortalecer la gestión financiera personal. Nuestro modelo combina asesoría especializada con herramientas tecnológicas que permiten tomar decisiones financieras informadas y sostenibles.
        </p>
        
        <p className="text-slate-700 text-xs sm:text-sm leading-relaxed mb-2">
          Diseñamos estrategias financieras sólidas, basadas en una cuidadosa selección de instrumentos de alta calidad en gestión patrimonial e inversiones. Combinamos visión global, análisis estratégico y tecnología avanzada para apoyar decisiones claras, seguras y bien fundamentadas.
        </p>

        <h3 className="font-['Outfit'] text-sm sm:text-base font-semibold text-slate-900 mt-3">Administración de finanzas personales, asesoría y planificación financiera</h3>
        <p className="text-slate-700 text-xs sm:text-sm leading-relaxed mb-2">
          En Abacco Financial brindamos asesoría financiera y administración de finanzas personales como base para lograr estabilidad económica y crecimiento patrimonial. Acompañamos a nuestros clientes en la organización de sus ingresos, optimización de gastos y gestión eficiente de sus recursos.
        </p>
        
        <p className="text-slate-700 text-xs sm:text-sm leading-relaxed mb-2">
          A través de un análisis integral, diseñamos estrategias personalizadas que incluyen planificación financiera, estructuración de inversiones, evaluación de oportunidades y riesgos, así como la definición de objetivos claros a corto, mediano y largo plazo.
        </p>
        
        <p className="text-slate-700 text-xs sm:text-sm leading-relaxed mb-3">
          Nuestro enfoque permite mejorar el flujo de caja, fortalecer la salud financiera y tomar decisiones más informadas, siempre alineadas al perfil, necesidades y metas de cada cliente.
        </p>
      </div>
      <button onClick={onBack} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mt-3 pt-3 flex-shrink-0">
        <ChevronRight size={20} className="rotate-180" />
        <span className="text-sm">Volver</span>
      </button>
    </div>
  );

  // Security Content (now Regulación)
  const SecurityContent = () => (
    <div className="flex flex-col h-full w-full">
      <div 
        className="space-y-3 w-full overflow-y-auto flex-1 -mr-4 pr-4"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(0,0,0,0.2) transparent'
        }}
      >
        <h2 className="font-['Outfit'] text-xl sm:text-2xl font-semibold text-slate-900 mb-3">Reguladores Internacionales</h2>
        
        <p className="text-slate-700 text-xs sm:text-sm leading-relaxed mb-3">
          Nuestras estructuras de inversión se alinean a los estándares regulatorios de los mercados financieros de Estados Unidos, adoptando las mejores prácticas de supervisión y protección al inversionista.
        </p>
        
        <p className="text-slate-700 text-xs sm:text-sm leading-relaxed mb-3">
          Entre las principales entidades regulatorias se encuentran:
        </p>
        
        <p className="text-slate-700 text-xs sm:text-sm leading-relaxed mb-2">
          <span className="font-bold">FINRA</span> - organismo que supervisa la conducta y transparencia de las firmas financieras.
        </p>
        
        <p className="text-slate-700 text-xs sm:text-sm leading-relaxed mb-2">
          <span className="font-bold">SEC</span> - principal regulador del mercado de valores de Estados Unidos.
        </p>
        
        <p className="text-slate-700 text-xs sm:text-sm leading-relaxed mb-2">
          <span className="font-bold">FDIC</span> - entidad que protege depósitos en instituciones bancarias aseguradas.
        </p>
        
        <p className="text-slate-700 text-xs sm:text-sm leading-relaxed mb-3">
          <span className="font-bold">SIPC</span> - sistema de protección al inversionista que cubre hasta $500.000 por cliente.
        </p>
        
        <p className="text-slate-700 text-xs sm:text-sm leading-relaxed mb-4">
          Este marco fortalece la seguridad y confianza en la gestión de los activos.
        </p>

        <h3 className="font-['Outfit'] text-lg sm:text-xl font-semibold text-slate-900 mt-4">Respaldo Nacional</h3>
        
        <p className="text-slate-700 text-xs sm:text-sm leading-relaxed mb-3">
          Abacco Financial opera en Ecuador bajo un estricto marco de cumplimiento normativo. La firma se encuentra regulada y supervisada por:
        </p>
        
        <ul className="text-slate-700 text-xs sm:text-sm leading-relaxed mb-3 list-disc list-inside space-y-1">
          <li>Superintendencia de Bancos.</li>
          <li>Superintendencia de Compañías, Valores y Seguros.</li>
          <li>Servicio de Rentas Internas (SRI)</li>
          <li>Unidad de Análisis Financiero y Económico (UAFE)</li>
        </ul>
        
        <p className="text-slate-700 text-xs sm:text-sm leading-relaxed mb-3">
          Garantizando transparencia, control y seguridad en todas nuestras operaciones.
        </p>
      </div>
      
      <button onClick={onBack} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mt-3 pt-3 flex-shrink-0">
        <ChevronRight size={20} className="rotate-180" />
        <span className="text-sm">Volver</span>
      </button>
    </div>
  );

  // Custodios Content
  const CustodiosContent = () => (
    <div className="flex flex-col h-full w-full">
      <div 
        className="space-y-3 w-full overflow-y-auto flex-1 -mr-4 pr-4"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(0,0,0,0.2) transparent'
        }}
      >
        <h2 className="font-['Outfit'] text-xl sm:text-2xl font-semibold text-slate-900 mb-3">Custodios</h2>
        
        <p className="text-slate-700 text-xs sm:text-sm leading-relaxed mb-3">
          Los activos se mantienen bajo custodia en instituciones financieras de primer nivel internacional, entre ellas:
        </p>
        
        <ul className="text-slate-700 text-xs sm:text-sm leading-relaxed mb-3 list-disc list-inside space-y-1">
          <li><span className="font-bold">Citibank</span></li>
          <li><span className="font-bold">J.P. Morgan</span></li>
          <li><span className="font-bold">Bank of America</span></li>
        </ul>
        
        <p className="text-slate-700 text-xs sm:text-sm leading-relaxed mb-3">
          Este esquema garantiza seguridad institucional, solidez operativa y cumplimiento normativo internacional.
        </p>
      </div>
      
      <button onClick={onBack} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mt-3 pt-3 flex-shrink-0">
        <ChevronRight size={20} className="rotate-180" />
        <span className="text-sm">Volver</span>
      </button>
    </div>
  );

  // Who We Are Content
  const WhoWeAreContent = () => (
    <div className="flex flex-col h-full w-full relative">
      <div 
        className="space-y-3 w-full overflow-y-auto flex-1 -mr-4 pr-4"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(0,0,0,0.2) transparent'
        }}
      >
        <h2 className="font-['Outfit'] text-xl sm:text-2xl font-semibold text-slate-900 mb-3">Quiénes somos</h2>
        <p className="text-slate-700 text-xs sm:text-sm leading-relaxed mb-3">
          ABACCO es una compañía financiera fintech, especialistas en asesoría financiera, administración de finanzas personales, banca privada y gestión de patrimonios. Promovemos una estrategia basada en diversificación global de activos y la selección de instrumentos financieros de alta calidad.
        </p>
        <h3 className="font-['Outfit'] text-base sm:text-lg font-semibold text-slate-900 mt-3">Misión</h3>
        <p className="text-slate-700 text-xs sm:text-sm leading-relaxed mb-3">
          Impulsamos la construcción y el crecimiento patrimonial de nuestros clientes mediante la administración estratégica de recursos, asesoría personalizada y soluciones financieras innovadoras, fomentando la confianza, la sostenibilidad y crecimiento económico con responsabilidad social.
        </p>
        <h3 className="font-['Outfit'] text-base sm:text-lg font-semibold text-slate-900 mt-3">Visión</h3>
        <p className="text-slate-700 text-xs sm:text-sm leading-relaxed">
          Para el año 2028, consolidarnos como una empresa de prestigio reconocida por su excelencia en la administración y asesoría financiera personalizada, contribuyendo al desarrollo económico y al fortalecimiento patrimonial de nuestros clientes, mediante una gestión eficiente de recursos, innovación constante, relaciones de confianza y un enfoque sostenible.
        </p>
      </div>
      <div className="flex items-center justify-between mt-4 pt-4">
        <button onClick={onBack} className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
          <ChevronRight size={20} className="rotate-180" />
          <span className="text-sm">Volver</span>
        </button>
        <img 
          src="https://customer-assets.emergentagent.com/job_simple-portfolio-hub/artifacts/eqao9mj2_ChatGPT%20Image%2027%20nov%202025%2C%2022_11_59.png" 
          alt="ABACCO Logo" 
          className="h-12 w-auto"
        />
      </div>
    </div>
  );

  // FAQ Content
  const FAQContent = () => (
    <div className="flex flex-col h-full w-full">
      <button onClick={onBack} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 flex-shrink-0">
        <ChevronRight size={20} className="rotate-180" />
        <span className="text-sm">Volver</span>
      </button>
      <h2 className="font-['Outfit'] text-2xl font-semibold text-slate-900 mb-4 flex-shrink-0">Preguntas Frecuentes</h2>
      <div 
        className="flex-1 overflow-y-auto -mr-4 pr-4"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(0,0,0,0.2) transparent'
        }}
      >
        <Accordion type="single" collapsible className="space-y-2">
          {faqItems.map((item, index) => (
            <AccordionItem 
              key={index} 
              value={`item-${index}`}
              className="bg-white/30 rounded-xl border-0 px-4 overflow-hidden"
            >
              <AccordionTrigger className="text-left text-sm font-medium text-slate-900 hover:no-underline py-3">
                {item.question}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 text-xs pb-3">
                {item.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );

  // Contact Content
  const ContactContent = () => (
    <div className="flex flex-col h-full w-full pt-6">
      <h2 className="font-['Outfit'] text-xl font-semibold text-slate-900 mb-6">Contacto</h2>
      
      <div className="text-center space-y-4 mb-6">
        <div>
          <h3 className="font-semibold text-slate-900 text-sm">Casa Matriz – Quito</h3>
          <p className="text-slate-700 text-xs">Av. Amazonas N35-43 y Juan Pablo Sanz, Piso 9.</p>
        </div>
        
        <div>
          <h3 className="font-semibold text-slate-900 text-sm">Guayaquil</h3>
          <p className="text-slate-700 text-xs">Joaquín José Orrantia González 100, Edificio<br/>100 Business Plaza, Piso 3, Oficina 303.</p>
        </div>
        
        <div>
          <h3 className="font-semibold text-slate-900 text-sm">Loja</h3>
          <p className="text-slate-700 text-xs">Calle Bolívar 201-15 y José Antonio Eguiguren,<br/>Edificio El Minero, 2do Piso, Oficina 204.</p>
        </div>
        
        <div>
          <h3 className="font-semibold text-slate-900 text-sm">Ibarra</h3>
          <p className="text-slate-700 text-xs">Calle Grijalva s/n y Chica Narváez, Piso 3.</p>
        </div>
      </div>

      {/* Invertir Ahora button with 3D logo */}
      <div className="flex justify-center mb-6 mt-16">
        <div className="relative">
          <img 
            src="https://customer-assets.emergentagent.com/job_simple-portfolio-hub/artifacts/h11rt9e7_ChatGPT%20Image%2016%20ene%202026%2C%2014_58_16.png" 
            alt="Logo" 
            className="absolute -top-14 left-1/2 -translate-x-1/2 w-40 h-40 object-contain -z-10"
          />
          <button 
            className="rounded-full px-6 py-2 font-semibold text-slate-900 relative z-10"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.6)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)'
            }}
          >
            Invertir Ahora
          </button>
        </div>
      </div>

      {/* Contact buttons */}
      <div className="flex justify-between items-center mb-6 mt-16">
        <a href="https://wa.me/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-900">
          <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          <span className="text-sm font-medium">Soporte Personal</span>
        </a>
        
        <a href="mailto:info@abaccofinancial.com" className="flex items-center gap-2 text-slate-900">
          <Mail size={20} />
          <span className="text-sm font-medium">Enviar Correo</span>
        </a>
      </div>
      
      <button onClick={onBack} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mt-auto">
        <ChevronRight size={20} className="rotate-180" />
        <span className="text-sm">Volver</span>
      </button>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case "portfolio": return <PortfolioContent />;
      case "seguridad": return <SecurityContent />;
      case "custodios": return <CustodiosContent />;
      case "quienes-somos": return <WhoWeAreContent />;
      case "faq": return <FAQContent />;
      case "contacto": return <ContactContent />;
      default: return (
        <nav className="space-y-6 w-full flex flex-col justify-center h-full">
          {menuSections.map((section, index) => (
            <motion.button
              key={section.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => onSelectSection(section.id)}
              className="flex items-center gap-4 text-slate-900 hover:text-slate-600 transition-colors group w-full"
              data-testid={`menu-item-${section.id}`}
            >
              <span className="font-['Outfit'] text-2xl sm:text-3xl font-medium">{section.name}</span>
              <ChevronRight size={28} className="text-slate-400 group-hover:text-slate-600 transition-colors" />
            </motion.button>
          ))}
        </nav>
      );
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          data-testid="menu-fullscreen"
          onClick={onClose}
        >
          {/* Menu container */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="rounded-3xl p-4 sm:p-6 md:p-8 w-full max-w-lg flex"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.38)',
              backdropFilter: 'blur(18px)',
              WebkitBackdropFilter: 'blur(18px)',
              height: 'calc(100vh - 120px)',
              maxHeight: '650px',
              marginBottom: '80px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {renderContent()}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Main App Component
function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [currentBgIndex, setCurrentBgIndex] = useState(0);

  // Background slider effect - pause when menu is open
  useEffect(() => {
    if (menuOpen) return; // Don't run slider when menu is open
    
    const interval = setInterval(() => {
      setCurrentBgIndex((prev) => (prev + 1) % BACKGROUND_IMAGES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [menuOpen]);

  const handleSelectSection = (sectionId) => {
    setActiveSection(sectionId);
  };

  const handleBack = () => {
    setActiveSection(null);
  };

  const handleCloseMenu = () => {
    setMenuOpen(false);
    setActiveSection(null);
  };

  return (
    <div className="App h-screen w-screen overflow-hidden relative" data-testid="app-container">
      {/* Background Image Slider - Vertical slide */}
      <div className="absolute inset-0 overflow-hidden">
        {BACKGROUND_IMAGES.map((img, index) => (
          <div 
            key={index}
            className="absolute inset-0 transition-transform duration-1000 ease-in-out bg-cover md:bg-cover bg-center"
            style={{ 
              backgroundImage: `url(${img})`,
              transform: index === currentBgIndex 
                ? 'translateY(0%)' 
                : index < currentBgIndex 
                  ? 'translateY(-100%)' 
                  : 'translateY(100%)'
            }}
          />
        ))}
      </div>
      
      {/* Light overlay for better text readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/5 via-transparent to-white/10" />

      {/* Cliente Button - Top Right */}
      <div className="absolute top-4 right-4 z-20">
        <a 
          href="http://abaccofinancial.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="px-5 py-2 rounded-full text-sm font-medium text-slate-700 shadow-sm transition-all hover:shadow-md active:scale-95 flex items-center gap-2"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)'
          }}
        >
          <User size={16} />
          Cliente
        </a>
      </div>

      {/* Main Content */}
      <div className="relative z-10 h-full flex flex-col justify-between">
        {/* Hero Content - Left aligned */}
        <div className="flex-1 flex items-center px-6 sm:px-12 lg:px-20">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-lg"
          >
            {/* Logo */}
            <div className="flex items-center gap-3 mb-6" data-testid="logo">
              <img src={LOGO_MAIN} alt="ABACCO Financial" className="h-4 w-auto" />
            </div>

            {/* Main Headline - Image */}
            <img 
              src="https://customer-assets.emergentagent.com/job_simple-portfolio-hub/artifacts/xh3nmuwp_infinity.png" 
              alt="infinite trust" 
              className="w-44 mb-8"
            />

            {/* CTA Button */}
            <button 
              className="group flex items-center gap-3 rounded-full px-4 py-2 shadow-lg hover:shadow-xl transition-all"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.38)',
                backdropFilter: 'blur(18px)',
                WebkitBackdropFilter: 'blur(18px)'
              }}
              data-testid="cta-button"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_simple-portfolio-hub/artifacts/2sxqwsra_Next%20Page.png" 
                alt="arrow" 
                className="w-7 h-7"
              />
              <div className="text-left">
                <div className="font-['Outfit'] font-semibold text-slate-900" style={{ fontSize: '14px' }}>Invertir Ahora</div>
                <div className="text-slate-900" style={{ fontSize: '6px' }}>Accede a Portafolios Globales</div>
              </div>
            </button>
          </motion.div>
        </div>

        {/* Bottom Navigation Bar - Fixed */}
        <div className="fixed bottom-0 left-0 right-0 z-30">
          <div className="mx-4 sm:mx-8 mb-4 sm:mb-6">
            <div 
              className="rounded-2xl px-6 py-2"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.38)',
                backdropFilter: 'blur(18px)',
                WebkitBackdropFilter: 'blur(18px)'
              }}
            >
              <div className="flex items-center justify-between">
                {/* Logo Icon */}
                <div data-testid="bottom-logo">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_simple-portfolio-hub/artifacts/qded2u59_ChatGPT%20Image%2027%20nov%202025%2C%2022_21_57.png" 
                    alt="ABACCO" 
                    className="h-10 w-auto"
                  />
                </div>

                {/* Menu Button */}
                <div className="relative">
                  <button
                    onClick={() => setMenuOpen(!menuOpen)}
                    className="flex flex-col items-center gap-1 text-slate-700 hover:text-slate-900 transition-colors"
                    data-testid="menu-button"
                  >
                    <img 
                      src="https://customer-assets.emergentagent.com/job_simple-portfolio-hub/artifacts/zzr0rti9_Structural.png" 
                      alt="menu icon" 
                      className="w-7 h-7"
                    />
                    <span style={{ fontSize: '11px' }}>Menu</span>
                  </button>
                </div>

                {/* Instagram Icon */}
                <a 
                  href="https://instagram.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-slate-700 hover:text-slate-900 transition-colors"
                  data-testid="instagram-link"
                >
                  <Instagram size={24} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Menu Fullscreen */}
      <MenuFullscreen 
        isOpen={menuOpen} 
        onClose={handleCloseMenu}
        onSelectSection={handleSelectSection}
        activeSection={activeSection}
        onBack={handleBack}
      />
    </div>
  );
}

export default App;
